import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { formatTimeAgo, formatNumber, getInitials } from '../lib/utils';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ArrowLeft, Radio, Eye, Heart, MessageCircle, 
  DollarSign, X, Loader2, Gift, Send, Share2,
  Pause, Play, UserPlus, Ban, MoreVertical, Sparkles, Trash2,
  Mic, MicOff, Video as VideoIcon, VideoOff, FlipHorizontal, VolumeX,
  Monitor, MonitorOff, Music, Swords, Shield, ShieldOff
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { FilterSelector, FilterPreview } from '../components/FilterSelector';
import { CAMERA_FILTERS, getFilterStyle } from '../lib/cameraFilters';
import useLiveStream from '../hooks/useLiveStream';
import SendGiftModal from '../components/gifts/SendGiftModal';
import BattlePanel from '../components/live/BattlePanel';
import BattleInviteModal from '../components/live/BattleInviteModal';
import MusicSelector from '../components/live/MusicSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tip packages
const TIP_PACKAGES = [
  { id: 'small', amount: 1, label: '$1' },
  { id: 'medium', amount: 5, label: '$5' },
  { id: 'large', amount: 10, label: '$10' },
  { id: 'huge', amount: 25, label: '$25' },
  { id: 'mega', amount: 50, label: '$50' }
];

// Virtual Gifts with prices
const VIRTUAL_GIFTS = [
  { id: 'heart', emoji: '❤️', name: 'Heart', price: 10, color: 'from-red-500 to-pink-500' },
  { id: 'fire', emoji: '🔥', name: 'Fire', price: 20, color: 'from-orange-500 to-red-500' },
  { id: 'star', emoji: '⭐', name: 'Star', price: 50, color: 'from-yellow-400 to-yellow-600' },
  { id: 'diamond', emoji: '💎', name: 'Diamond', price: 100, color: 'from-blue-400 to-cyan-500' },
  { id: 'crown', emoji: '👑', name: 'Crown', price: 200, color: 'from-yellow-500 to-amber-600' },
  { id: 'rocket', emoji: '🚀', name: 'Rocket', price: 500, color: 'from-purple-500 to-pink-500' },
  { id: 'unicorn', emoji: '🦄', name: 'Unicorn', price: 1000, color: 'from-pink-400 to-purple-500' },
  { id: 'trophy', emoji: '🏆', name: 'Trophy', price: 2000, color: 'from-yellow-500 to-orange-500' },
];

export default function LiveStreamPage() {
  const { streamId } = useParams();
  const { user, token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  const commentsEndRef = useRef(null);
  
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTipModal, setShowTipModal] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedTip, setSelectedTip] = useState(null);
  const [tipMessage, setTipMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [likes, setLikes] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [floatingGifts, setFloatingGifts] = useState([]);
  const [floatingHearts, setFloatingHearts] = useState([]);
  const [searchUsers, setSearchUsers] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [showFilterPicker, setShowFilterPicker] = useState(false);
  const [coHosts, setCoHosts] = useState([]); // Up to 20 co-hosts
  const [topViewers, setTopViewers] = useState([]); // Up to 20 viewer avatars

  const [coinBalance, setCoinBalance] = useState(0);

  // === Live streaming state ===
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [liveViewerCount, setLiveViewerCount] = useState(0);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [streamError, setStreamError] = useState(null);

  // Gift modal + animation state (uses showGiftModal & floatingGifts declared above)
  const [topSupporters, setTopSupporters] = useState([]);
  const [streamGiftTotal, setStreamGiftTotal] = useState(0);

  // === New features state ===
  const [activeBattle, setActiveBattle] = useState(null);
  const [showBattleInvite, setShowBattleInvite] = useState(false);
  const [musicState, setMusicState] = useState(null);
  const [showMusicSelector, setShowMusicSelector] = useState(false);
  const [aiModerationEnabled, setAiModerationEnabled] = useState(true);
  const musicAudioRef = useRef(null);
  // Browsers block autoplay-with-sound — start the viewer's video muted and
  // expose an explicit "Tap to unmute" button so audio becomes audible after
  // a single user gesture (TikTok/Instagram pattern).
  const [isViewerMuted, setIsViewerMuted] = useState(true);

  const isOwner = stream?.user_id === user?.id;
  const isVideoStream = true; // For now, all live streams are video

  // Connect WebRTC + WebSocket signaling
  const { localStream, toggleMic, toggleCamera, flipCamera,
          startScreenShare, stopScreenShare, isScreenSharing } = useLiveStream({
    streamId,
    userId: user?.id,
    hostId: stream?.user_id,
    isHost: !!stream && isOwner,
    isVideo: isVideoStream,
    onRemoteStream: (s) => {
      setRemoteStream(s);
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = s;
    },
    onViewerCount: (count) => setLiveViewerCount(count),
    onComment: (c) => {
      setComments((prev) => (prev.some((p) => p.id === c.id) ? prev : [...prev, c]));
    },
    onCommentBlocked: (reason) => {
      toast.error(`Comment blocked: ${reason}`);
    },
    onLiveLike: (d) => {
      // Skip echo of *our own* tap on this device — we already animated locally.
      const isMyTap = d?.user_id === user?.id;
      // Keep total likes counter in sync with server's authoritative number
      if (typeof d?.likes_count === 'number') {
        setLikes(d.likes_count);
      }
      if (isMyTap) return;
      // Show a floating heart for every tap from another viewer (the host
      // also receives these and sees the hearts animate).
      const id = Date.now() + Math.random();
      const heartEmojis = ['❤️', '💕', '💖', '💗', '💓', '🩷'];
      const emoji = heartEmojis[Math.floor(Math.random() * heartEmojis.length)];
      setFloatingHearts((prev) => [...prev, {
        id, emoji,
        x: Math.random() * 60 + 20,
        scale: 0.8 + Math.random() * 0.4,
      }]);
      setTimeout(() => {
        setFloatingHearts((prev) => prev.filter((h) => h.id !== id));
      }, 2000);
    },
    onLiveGift: (gift) => {
      // Floating animation
      const flyId = `${gift.id}-${Date.now()}`;
      setFloatingGifts((prev) => [...prev, { ...gift, flyId }]);
      setTimeout(() => {
        setFloatingGifts((prev) => prev.filter((g) => g.flyId !== flyId));
      }, 4500);
      // Update stream total
      setStreamGiftTotal((t) => t + (gift.price || 0));
      // Refresh top supporters
      refreshTopSupporters();
    },
    onMusic: (m) => {
      setMusicState(m);
    },
    onMusicStop: () => {
      setMusicState(null);
    },
    onBattleStarted: (b) => {
      setActiveBattle(b);
      toast.success('Live Battle started!');
    },
    onBattleUpdate: (b) => {
      setActiveBattle(b);
    },
    onBattleEnded: (b) => {
      setActiveBattle(b);
      const meId = user?.id;
      if (b.winner_id === meId) toast.success('🏆 You won the battle!');
      else if (b.winner_id && (meId === b.host1_id || meId === b.host2_id)) toast.info('Battle ended');
    },
    onKicked: () => {
      toast.error('You have been removed from this live by the host');
      navigate('/live');
    },
    onMuted: () => {
      toast.error('You have been muted in this live');
    },
    onEnded: () => {
      toast.info('Live ended');
      navigate('/live');
    },
    onError: (msg) => setStreamError(msg),
  });

  // Attach local video for the host
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // Re-attach remote stream if video element re-mounts
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream, loading]);

  // === Music: play/pause synced audio element ===
  useEffect(() => {
    if (!musicAudioRef.current) return;
    const audio = musicAudioRef.current;
    if (musicState?.url) {
      // Compute offset from started_at so newcomers join roughly in-sync
      let offset = 0;
      try {
        if (musicState.started_at) {
          const elapsed = (Date.now() - new Date(musicState.started_at).getTime()) / 1000;
          offset = Math.max(0, elapsed % (musicState.duration || 9999));
        }
      } catch {}
      audio.src = musicState.url;
      audio.volume = musicState.volume ?? 0.35;
      audio.loop = true;
      audio.currentTime = offset;
      audio.play().catch(() => {
        // Autoplay blocked — silently fail; user can tap once to unblock
      });
    } else {
      audio.pause();
      audio.src = '';
    }
  }, [musicState]);

  // Fetch active battle + music when stream loads; auto-accept polling
  useEffect(() => {
    if (!streamId || !token) return;
    let cancelled = false;
    const fetchBattle = async () => {
      try {
        const r = await axios.get(`${API}/live/battles/stream/${streamId}/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && r.data) setActiveBattle(r.data);
      } catch {}
    };
    (async () => {
      await fetchBattle();
      try {
        const r = await axios.get(`${API}/live/music/${streamId}/current`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.data?.url) setMusicState(r.data);
      } catch {}
    })();
    // Poll every 5s for pending battle invites (lightweight fallback to WS)
    const intv = setInterval(fetchBattle, 5000);
    return () => { cancelled = true; clearInterval(intv); };
  }, [streamId, token]);


  useEffect(() => {
    // Fetch user's coin balance
    const fetchCoinBalance = async () => {
      try {
        const response = await axios.get(`${API}/coins/balance`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCoinBalance(response.data.coins);
      } catch (error) {
        // Ignore
      }
    };
    fetchCoinBalance();
  }, [token]);

  const fetchStream = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/live/${streamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStream(response.data);
      setLikes(response.data.likes_count || 0);
      
      // Join stream as viewer
      if (response.data.user_id !== user?.id) {
        await axios.post(`${API}/live/${streamId}/join`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Failed to fetch stream:', error);
      toast.error('Live not found');
      navigate('/live');
    } finally {
      setLoading(false);
    }
  }, [streamId, token, user?.id, navigate]);

  const fetchComments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/live/${streamId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out blocked users' comments
      const filteredComments = (response.data || []).filter(c => !blockedUsers.includes(c.user_id));
      setComments(filteredComments);
    } catch (error) {
      // Comments endpoint might not exist yet
    }
  }, [streamId, token, blockedUsers]);

  const fetchViewers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/live/${streamId}/viewers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Get top 20 viewers to show avatars
      setTopViewers((response.data || []).slice(0, 20));
    } catch (error) {
      // Viewers endpoint might not exist yet
    }
  }, [streamId, token]);

  const fetchCoHosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/live/${streamId}/cohosts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Get up to 20 co-hosts
      setCoHosts((response.data || []).slice(0, 20));
    } catch (error) {
      // Co-hosts endpoint might not exist yet
    }
  }, [streamId, token]);

  const refreshTopSupporters = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/coin-shop/top-supporters/live/${streamId}?limit=3`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTopSupporters(res.data || []);
    } catch (e) {
      // ignore
    }
  }, [streamId, token]);

  useEffect(() => {
    fetchStream();
    fetchComments();
    fetchViewers();
    fetchCoHosts();
    refreshTopSupporters();
    // Less aggressive polling (real-time WS handles most updates)
    const interval = setInterval(() => {
      fetchViewers();
      fetchCoHosts();
    }, 10000);
    
    return () => {
      clearInterval(interval);
      if (stream && stream.user_id !== user?.id) {
        axios.post(`${API}/live/${streamId}/leave`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  const handleEndStream = async () => {
    try {
      await axios.post(`${API}/live/${streamId}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Live ended!');
      navigate('/live');
    } catch (error) {
      toast.error('Error ending live');
    }
  };

  const handleLike = async () => {
    // Allow multiple taps - TikTok style
    setLikes(prev => prev + 1);
    
    // Show floating heart animation at random position
    const id = Date.now() + Math.random();
    const heartEmojis = ['❤️', '💕', '💖', '💗', '💓', '🩷'];
    const emoji = heartEmojis[Math.floor(Math.random() * heartEmojis.length)];
    setFloatingHearts(prev => [...prev, { 
      id, 
      emoji, 
      x: Math.random() * 60 + 20,
      scale: 0.8 + Math.random() * 0.4
    }]);
    
    setTimeout(() => {
      setFloatingHearts(prev => prev.filter(h => h.id !== id));
    }, 2000);

    // Send like to server (debounced)
    try {
      await axios.post(`${API}/live/${streamId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Ignore errors
    }
  };

  const handleSendComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    
    const newComment = {
      id: Date.now(),
      user_id: user.id,
      username: user.username,
      display_name: user.display_name,
      avatar: user.avatar,
      content: comment,
      created_at: new Date().toISOString()
    };
    
    setComments(prev => [...prev, newComment]);
    setComment('');

    try {
      await axios.post(`${API}/live/${streamId}/comment`, 
        { content: comment },
        { headers: { Authorization: `Bearer ${token}` }}
      );
    } catch (error) {
      // Ignore errors
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/live/${streamId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: stream.title,
          text: `Watch ${stream.display_name || stream.username} live on FityH!`,
          url: shareUrl
        });
      } catch (error) {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied!');
    }
  };

  const handleSendGift = async (gift) => {
    // Check coin balance
    if (coinBalance < gift.price) {
      toast.error(`Not enough coins! You need ${gift.price} coins.`);
      navigate('/coins');
      return;
    }

    setSending(true);
    try {
      // Send coins to streamer
      await axios.post(`${API}/coins/send`, 
        { 
          receiver_id: stream.user_id, 
          amount: gift.price,
          stream_id: streamId 
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Update local balance
      setCoinBalance(prev => prev - gift.price);
      
      // Show floating gift animation
      const id = Date.now();
      setFloatingGifts(prev => [...prev, { id, emoji: gift.emoji, x: Math.random() * 80 + 10 }]);
      setTimeout(() => {
        setFloatingGifts(prev => prev.filter(g => g.id !== id));
      }, 3000);

      toast.success(`Sent ${gift.emoji} ${gift.name} (${gift.price} coins)!`);
      setShowGiftModal(false);

      // Also record in live_gifts
      await axios.post(`${API}/live/${streamId}/gift`, 
        { gift_id: gift.id, amount: gift.price },
        { headers: { Authorization: `Bearer ${token}` }}
      ).catch(() => {});
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send gift');
    } finally {
      setSending(false);
    }
  };

  const handleSearchUsers = async (query) => {
    setSearchUsers(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/users/search?q=${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.filter(u => u.id !== user.id));
    } catch (error) {
      // Ignore
    }
  };

  const handleInviteUser = async (invitedUser) => {
    toast.success(`Invited ${invitedUser.display_name || invitedUser.username} to join!`);
    setShowInviteModal(false);
    setSearchUsers('');
    setSearchResults([]);
  };

  const handleBlockUser = async (userId, username) => {
    setBlockedUsers(prev => [...prev, userId]);
    setComments(prev => prev.filter(c => c.user_id !== userId));
    toast.success(`${username} blocked from this live`);
    
    // Notify server
    try {
      await axios.post(`${API}/live/${streamId}/block`, 
        { user_id: userId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
    } catch (error) {
      // Ignore
    }
  };

  const handleDeleteComment = async (commentId, userId) => {
    setComments(prev => prev.filter(c => c.id !== commentId));
    toast.success('Comment deleted');
    
    try {
      await axios.delete(`${API}/live/${streamId}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Ignore
    }
  };

  const handleSendTip = async () => {
    if (!selectedTip) return;
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/tips/${stream.user_id}/checkout`, null, {
        params: {
          package_id: selectedTip,
          message: tipMessage,
          origin_url: window.location.origin
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.demo_mode) {
        toast.success(`Tip sent!`);
        setShowTipModal(false);
        setSelectedTip(null);
        setTipMessage('');
      } else if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      }
    } catch (error) {
      toast.error('Error sending tip');
    } finally {
      setSending(false);
    }
  };

  // Moderation handlers (host only)
  const handleKickUser = async (uid, username) => {
    try {
      await axios.post(`${API}/live/${streamId}/kick`,
        { user_id: uid },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`${username} kicked from the live`);
    } catch (e) {
      toast.error('Failed to kick user');
    }
  };

  const handleMuteUser = async (uid, username) => {
    try {
      await axios.post(`${API}/live/${streamId}/mute_user`,
        { user_id: uid },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`${username} muted`);
    } catch (e) {
      toast.error('Failed to mute user');
    }
  };

  const handleToggleMic = () => {
    const muted = toggleMic();
    setIsMicMuted(muted);
    toast.info(muted ? 'Microphone muted' : 'Microphone on');
  };

  const handleToggleCamera = () => {
    const off = toggleCamera();
    setIsCameraOff(off);
    toast.info(off ? 'Camera off' : 'Camera on');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!stream) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white">
        <p className="text-lg">Live not found or ended</p>
        <Button onClick={() => navigate('/live')} variant="ghost" className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col" data-testid="live-stream-page">
      {/* Video Area */}
      <div 
        className="relative flex-1 min-h-[60vh] bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center"
        style={{ filter: getFilterStyle(selectedFilter) }}
        onClick={handleLike}
      >
        {/* === REAL VIDEO STREAM === */}
        {isOwner ? (
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover bg-black"
            style={{ transform: 'scaleX(-1)' }}
            data-testid="live-host-video"
          />
        ) : (
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            muted={isViewerMuted}
            className="absolute inset-0 w-full h-full object-cover bg-black"
            data-testid="live-viewer-video"
          />
        )}

        {/* Unmute button — starts muted to allow autoplay; user taps to enable audio */}
        {!isOwner && remoteStream && isViewerMuted && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsViewerMuted(false);
              if (remoteVideoRef.current) {
                remoteVideoRef.current.muted = false;
                remoteVideoRef.current.play().catch(() => {});
              }
            }}
            data-testid="live-unmute-btn"
            className="absolute top-4 right-4 z-30 flex items-center gap-2 bg-black/70
                       backdrop-blur-sm text-white rounded-full px-4 py-2
                       ring-2 ring-white/30 shadow-lg active:scale-95 transition"
          >
            <VolumeX className="w-4 h-4" />
            <span className="text-sm font-semibold">Tap to unmute</span>
          </button>
        )}

        {/* Connection / fallback overlay */}
        {!isOwner && !remoteStream && !isPaused && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 z-[1] pointer-events-none">
            <Loader2 className="w-10 h-10 text-white animate-spin" />
            <p className="text-white/80 mt-3">Connecting to live stream...</p>
          </div>
        )}

        {/* Host error overlay */}
        {isOwner && streamError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center p-8 bg-black/80 z-[2]">
            <VideoOff className="w-16 h-16 text-white/50" />
            <p className="text-white/80">{streamError}</p>
          </div>
        )}

        {/* Camera off overlay (host with camera disabled) */}
        {isOwner && isCameraOff && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center p-8 bg-black/70 z-[1]">
            <VideoOff className="w-16 h-16 text-white/60" />
            <p className="text-white/80 text-sm">Camera off</p>
          </div>
        )}

        {/* Filter overlay */}
        {CAMERA_FILTERS.find(f => f.id === selectedFilter)?.overlay && (
          <div 
            className="absolute inset-0 pointer-events-none z-0"
            style={{ backgroundColor: CAMERA_FILTERS.find(f => f.id === selectedFilter).overlay }}
          />
        )}
        
        {/* Floating Gifts Animation */}
        {floatingGifts.map(gift => (
          <div
            key={gift.id}
            className="absolute bottom-20 text-4xl animate-float-up pointer-events-none"
            style={{ left: `${gift.x}%` }}
          >
            {gift.emoji}
          </div>
        ))}
        
        {/* Floating Hearts Animation - TikTok Style */}
        {floatingHearts.map(heart => (
          <div
            key={heart.id}
            className="absolute bottom-32 pointer-events-none z-50"
            style={{ 
              left: `${heart.x}%`,
              transform: `scale(${heart.scale})`,
              animation: 'floatUp 2s ease-out forwards'
            }}
          >
            <span className="text-4xl">{heart.emoji}</span>
          </div>
        ))}
        
        {/* Co-Hosts Grid - Up to 20 people */}
        {coHosts.length > 0 && (
          <div className="absolute top-16 left-4 right-4 z-10">
            <div className={`grid gap-2 ${
              coHosts.length <= 4 ? 'grid-cols-2' : 
              coHosts.length <= 9 ? 'grid-cols-3' : 
              coHosts.length <= 16 ? 'grid-cols-4' : 'grid-cols-5'
            }`}>
              {coHosts.slice(0, 20).map((host, idx) => (
                <div 
                  key={host.id || idx}
                  className="relative aspect-video bg-black/50 rounded-lg overflow-hidden ring-2 ring-pink-500/50"
                >
                  <Avatar className="w-full h-full">
                    <AvatarImage src={host.avatar} className="object-cover" />
                    <AvatarFallback className="text-xs bg-primary/30">
                      {getInitials(host.display_name || host.username)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5">
                    <p className="text-[10px] text-white truncate">@{host.username}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Viewer Avatars - Bottom Left - Up to 20 */}
        {topViewers.length > 0 && (
          <div className="absolute bottom-4 left-4 flex items-center gap-1 z-10">
            <div className="flex -space-x-2">
              {topViewers.slice(0, 8).map((viewer, idx) => (
                <Avatar 
                  key={viewer.id || idx} 
                  className="w-8 h-8 ring-2 ring-black"
                >
                  <AvatarImage src={viewer.avatar} />
                  <AvatarFallback className="text-[10px] bg-primary/50">
                    {getInitials(viewer.display_name || viewer.username)}
                  </AvatarFallback>
                </Avatar>
              ))}
            </div>
            {topViewers.length > 8 && (
              <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white text-xs">
                +{topViewers.length - 8}
              </div>
            )}
          </div>
        )}

        {/* Back button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm text-white rounded-full hover:bg-black/70 z-10"
          onClick={() => navigate('/live')}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        
        {/* Top Right - Live badge & viewers */}
        <div className="absolute top-4 right-4 flex items-center gap-3 z-10">
          <div className="flex items-center gap-2 bg-destructive text-white px-4 py-2 rounded-full font-bold">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            LIVE
          </div>
          <div className="flex items-center gap-2 bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-full">
            <Eye className="w-4 h-4" />
            {formatNumber(liveViewerCount || stream.viewer_count)}
          </div>
        </div>
        
        {/* Streamer info - only show when no real video yet */}
        {!remoteStream && !isOwner && (
          <div className="text-center text-white relative z-[1]">
            <Avatar className="w-32 h-32 mx-auto ring-4 ring-white/30">
              <AvatarImage src={stream.avatar} />
              <AvatarFallback className="text-4xl bg-primary/50">
                {getInitials(stream.display_name || stream.username)}
              </AvatarFallback>
            </Avatar>
            <h2 className="mt-4 text-2xl font-bold">{stream.title}</h2>
            <p className="text-white/70 mt-1">@{stream.username}</p>
          </div>
        )}
        
        {/* Stream title for viewers (top-left under back btn) */}
        {(remoteStream || isOwner) && (
          <div className="absolute top-16 left-4 max-w-[60%] z-[5] pointer-events-none">
            <div className="bg-black/40 backdrop-blur-sm rounded-2xl px-3 py-2">
              <p className="text-white text-sm font-semibold truncate">{stream.title}</p>
              <p className="text-white/70 text-xs">@{stream.username}</p>
            </div>
          </div>
        )}

        {/* Active Live Battle panel (top-center) */}
        {activeBattle && (activeBattle.status === 'active' || activeBattle.status === 'ended') && (
          <BattlePanel
            battle={activeBattle}
            onEnded={async () => {
              // Auto-finalize on timeout (any participant or viewer triggers it on UI countdown)
              try {
                const r = await axios.get(`${API}/live/battles/${activeBattle.id}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                setActiveBattle(r.data);
              } catch {}
            }}
          />
        )}

        {/* Pending battle invite — show accept/decline to host2 only */}
        {activeBattle && activeBattle.status === 'pending' && isOwner && user?.id === activeBattle.host2_id && (
          <div
            data-testid="battle-invite-banner"
            className="absolute top-16 left-1/2 -translate-x-1/2 z-20 w-[92%] max-w-md
                       bg-gradient-to-r from-pink-500/95 to-purple-500/95 backdrop-blur-md
                       rounded-2xl p-3 shadow-2xl ring-2 ring-white/30"
          >
            <div className="flex items-center gap-3">
              <Swords className="w-5 h-5 text-white flex-shrink-0" />
              <p className="text-white text-sm font-semibold flex-1">
                @{activeBattle.host1_username} challenged you to a {Math.round((activeBattle.duration||180)/60)} min battle!
              </p>
            </div>
            <div className="flex gap-2 mt-2">
              <Button
                size="sm"
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold"
                data-testid="accept-battle-btn"
                onClick={async () => {
                  try {
                    const r = await axios.post(
                      `${API}/live/battles/${activeBattle.id}/accept`, {},
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setActiveBattle(r.data);
                    toast.success('Battle started!');
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Could not accept');
                  }
                }}
              >
                Accept
              </Button>
              <Button
                size="sm"
                variant="destructive"
                className="flex-1"
                data-testid="decline-battle-btn"
                onClick={async () => {
                  try {
                    await axios.post(
                      `${API}/live/battles/${activeBattle.id}/decline`, {},
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setActiveBattle(null);
                  } catch (e) {
                    toast.error('Could not decline');
                  }
                }}
              >
                Decline
              </Button>
            </div>
          </div>
        )}

        {/* Now-playing music banner */}
        {musicState && (
          <div
            data-testid="music-banner"
            className="absolute top-4 right-4 z-[6] bg-pink-500/90 backdrop-blur-sm rounded-full px-3 py-1.5 flex items-center gap-2 max-w-[60%] pointer-events-none"
          >
            <Music className="w-3.5 h-3.5 text-white animate-pulse" />
            <p className="text-white text-xs font-semibold truncate">{musicState.title}</p>
          </div>
        )}
        
        {/* Owner Controls */}
        {isOwner && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="secondary"
              size="icon"
              className={`rounded-full ${isMicMuted ? 'bg-red-500/80 hover:bg-red-600' : 'bg-white/20 hover:bg-white/30'} backdrop-blur-sm`}
              onClick={handleToggleMic}
              data-testid="toggle-mic-btn"
              title={isMicMuted ? 'Unmute mic' : 'Mute mic'}
            >
              {isMicMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </Button>
            <Button
              variant="secondary"
              size="icon"
              className={`rounded-full ${isCameraOff ? 'bg-red-500/80 hover:bg-red-600' : 'bg-white/20 hover:bg-white/30'} backdrop-blur-sm`}
              onClick={handleToggleCamera}
              data-testid="toggle-camera-btn"
              title={isCameraOff ? 'Camera on' : 'Camera off'}
            >
              {isCameraOff ? <VideoOff className="w-5 h-5" /> : <VideoIcon className="w-5 h-5" />}
            </Button>
            <Button
              variant="secondary"
              size="icon"
              className="rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30"
              onClick={flipCamera}
              data-testid="flip-camera-btn"
              title="Flip camera"
            >
              <FlipHorizontal className="w-5 h-5" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              className="rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30"
              onClick={() => setShowFilterPicker(!showFilterPicker)}
              data-testid="filter-btn"
              title="Filters"
            >
              <Sparkles className="w-5 h-5" />
            </Button>
            <Button
              variant="secondary"
              className="rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30"
              onClick={() => setShowInviteModal(true)}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Invite
            </Button>
            <Button
              variant="secondary"
              size="icon"
              className={`rounded-full ${isScreenSharing ? 'bg-blue-500/80 hover:bg-blue-600' : 'bg-white/20 hover:bg-white/30'} backdrop-blur-sm`}
              onClick={async () => {
                if (isScreenSharing) {
                  await stopScreenShare();
                  toast.info('Screen share stopped');
                } else {
                  const ok = await startScreenShare();
                  if (ok) toast.success('Sharing your screen');
                  else toast.error('Could not start screen share');
                }
              }}
              data-testid="screen-share-btn"
              title={isScreenSharing ? 'Stop screen share' : 'Share screen'}
            >
              {isScreenSharing ? <MonitorOff className="w-5 h-5" /> : <Monitor className="w-5 h-5" />}
            </Button>
            <Button
              variant="secondary"
              size="icon"
              className={`rounded-full ${musicState ? 'bg-pink-500/80 hover:bg-pink-600' : 'bg-white/20 hover:bg-white/30'} backdrop-blur-sm`}
              onClick={() => setShowMusicSelector(true)}
              data-testid="music-btn"
              title="Background music"
            >
              <Music className="w-5 h-5" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              className={`rounded-full ${activeBattle ? 'bg-purple-500/80 hover:bg-purple-600' : 'bg-white/20 hover:bg-white/30'} backdrop-blur-sm`}
              onClick={() => {
                if (activeBattle && activeBattle.status === 'active') {
                  toast.info('Already in a battle');
                } else {
                  setShowBattleInvite(true);
                }
              }}
              data-testid="battle-btn"
              title="Start a battle"
            >
              <Swords className="w-5 h-5" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              className={`rounded-full ${aiModerationEnabled ? 'bg-emerald-500/80 hover:bg-emerald-600' : 'bg-white/20 hover:bg-white/30'} backdrop-blur-sm`}
              onClick={async () => {
                const next = !aiModerationEnabled;
                try {
                  await axios.post(`${API}/live/${streamId}/ai_moderation`, { enabled: next }, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  setAiModerationEnabled(next);
                  toast.success(next ? 'AI moderation ON' : 'AI moderation OFF');
                } catch {
                  toast.error('Could not update moderation');
                }
              }}
              data-testid="ai-mod-btn"
              title={aiModerationEnabled ? 'AI moderation: ON' : 'AI moderation: OFF'}
            >
              {aiModerationEnabled ? <Shield className="w-5 h-5" /> : <ShieldOff className="w-5 h-5" />}
            </Button>
            <Button
              variant="destructive"
              className="rounded-full px-6"
              onClick={handleEndStream}
              data-testid="end-stream-btn"
            >
              <X className="w-4 h-4 mr-2" />
              End Live
            </Button>
          </div>
        )}
        
        {/* Filter Picker for Owner */}
        {isOwner && showFilterPicker && (
          <div className="absolute bottom-24 left-4 right-4 bg-black/80 backdrop-blur-xl rounded-2xl p-4 z-20">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Filters
              </h4>
              <Button variant="ghost" size="sm" onClick={() => setShowFilterPicker(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {CAMERA_FILTERS.map(filter => (
                <button
                  key={filter.id}
                  onClick={() => setSelectedFilter(filter.id)}
                  className={`flex-shrink-0 flex flex-col items-center gap-1 p-2 rounded-lg transition-all ${
                    selectedFilter === filter.id ? 'bg-primary/30 ring-2 ring-primary' : 'bg-white/10'
                  }`}
                >
                  <span className="text-2xl">{filter.icon}</span>
                  <span className="text-xs text-white">{filter.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Right Side Actions */}
        <div className="absolute right-4 bottom-20 flex flex-col items-center gap-4">
          {/* Like */}
          <button
            onClick={handleLike}
            className={`flex flex-col items-center ${hasLiked ? 'text-red-500' : 'text-white'}`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${hasLiked ? 'bg-red-500/20' : 'bg-white/20'}`}>
              <Heart className={`w-6 h-6 ${hasLiked ? 'fill-current' : ''}`} />
            </div>
            <span className="text-xs mt-1">{formatNumber(likes)}</span>
          </button>

          {/* Comments */}
          <button className="flex flex-col items-center text-white">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <MessageCircle className="w-6 h-6" />
            </div>
            <span className="text-xs mt-1">{comments.length}</span>
          </button>

          {/* Share */}
          <button onClick={handleShare} className="flex flex-col items-center text-white">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Share2 className="w-6 h-6" />
            </div>
            <span className="text-xs mt-1">Share</span>
          </button>

          {/* Gift */}
          {!isOwner && (
            <button onClick={() => setShowGiftModal(true)} className="flex flex-col items-center text-white">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center">
                <Gift className="w-6 h-6" />
              </div>
              <span className="text-xs mt-1">Gift</span>
            </button>
          )}
        </div>
      </div>
      
      {/* Comments Section */}
      <div className="bg-black/90 backdrop-blur-sm border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          {/* Comments List */}
          <div className="h-40 overflow-y-auto p-4 space-y-3">
            {comments.filter(c => !blockedUsers.includes(c.user_id)).map((c) => (
              <div key={c.id} className="flex items-start gap-2 text-white">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={c.avatar} />
                  <AvatarFallback className="text-xs bg-primary/50">
                    {getInitials(c.display_name || c.username)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <span className="font-semibold text-primary text-sm">{c.display_name || c.username}</span>
                  <p className="text-sm text-white/90">{c.content}</p>
                </div>
                {isOwner && c.user_id !== user.id && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="w-6 h-6 text-white/50">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem 
                        onClick={() => handleDeleteComment(c.id, c.user_id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Comment
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleMuteUser(c.user_id, c.username)}
                      >
                        <VolumeX className="w-4 h-4 mr-2" />
                        Mute @{c.username}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleKickUser(c.user_id, c.username)}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Kick @{c.username}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleBlockUser(c.user_id, c.username)}
                        className="text-destructive"
                      >
                        <Ban className="w-4 h-4 mr-2" />
                        Block @{c.username}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            ))}
            <div ref={commentsEndRef} />
          </div>
          
          {/* Comment Input */}
          <form onSubmit={handleSendComment} className="p-4 border-t border-white/10 flex gap-2">
            <Input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Say something..."
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-full"
            />
            <Button type="submit" size="icon" className="rounded-full" disabled={!comment.trim()}>
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>

      {/* (Old emoji gift modal removed — replaced by SendGiftModal below for premium Coin Shop gifts) */}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-3xl p-6 w-full max-w-md animate-scale-in">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <UserPlus className="w-6 h-6 text-primary" />
                Invite to Live
              </h3>
              <Button variant="ghost" size="icon" onClick={() => setShowInviteModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <Input
              value={searchUsers}
              onChange={(e) => handleSearchUsers(e.target.value)}
              placeholder="Search users..."
              className="rounded-full mb-4"
            />
            
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {searchResults.map(u => (
                <div key={u.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-secondary">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={u.avatar} />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {getInitials(u.display_name || u.username)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold">{u.display_name || u.username}</p>
                      <p className="text-sm text-muted-foreground">@{u.username}</p>
                    </div>
                  </div>
                  <Button size="sm" className="rounded-full" onClick={() => handleInviteUser(u)}>
                    Invite
                  </Button>
                </div>
              ))}
              {searchUsers.length >= 2 && searchResults.length === 0 && (
                <p className="text-center text-muted-foreground py-4">No users found</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tip Modal */}
      {showTipModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-3xl p-6 w-full max-w-md animate-scale-in">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-primary" />
                Send a Tip
              </h3>
              <Button variant="ghost" size="icon" onClick={() => setShowTipModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="grid grid-cols-3 gap-3 mb-6">
              {TIP_PACKAGES.map(tip => (
                <button
                  key={tip.id}
                  onClick={() => setSelectedTip(tip.id)}
                  className={`p-4 rounded-2xl border-2 transition-all ${
                    selectedTip === tip.id
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <span className="text-xl font-bold">{tip.label}</span>
                </button>
              ))}
            </div>
            
            <Input
              placeholder="Add a message (optional)"
              value={tipMessage}
              onChange={(e) => setTipMessage(e.target.value)}
              className="rounded-2xl mb-6"
            />
            
            <Button
              onClick={handleSendTip}
              disabled={!selectedTip || sending}
              className="w-full rounded-full bg-gradient-to-r from-yellow-500 to-orange-500"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>Send {selectedTip ? TIP_PACKAGES.find(t => t.id === selectedTip)?.label : 'Tip'}</>
              )}
            </Button>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes float-up {
          0% { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-200px) scale(1.5); }
        }
        .animate-float-up {
          animation: float-up 2s ease-out forwards;
        }
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        @keyframes gift-fly-in {
          0%   { opacity: 0; transform: translate(-50%, 60%) scale(0.4) rotate(-25deg); }
          15%  { opacity: 1; transform: translate(-50%, 0) scale(1.1) rotate(0deg); }
          75%  { opacity: 1; transform: translate(-50%, 0) scale(1) rotate(0deg); }
          100% { opacity: 0; transform: translate(-50%, -120px) scale(0.8) rotate(15deg); }
        }
        .animate-gift-fly-in {
          animation: gift-fly-in 4.5s ease-in-out forwards;
        }
      `}</style>

      {/* Floating gift animations */}
      <div className="pointer-events-none fixed inset-0 z-[150] overflow-hidden">
        {floatingGifts.map((g, idx) => (
          <div
            key={g.flyId}
            className="absolute left-1/2 bottom-32 animate-gift-fly-in"
            style={{
              left: `${30 + (idx * 15) % 50}%`,
              animationDelay: `${idx * 0.1}s`,
            }}
          >
            <div className="flex flex-col items-center">
              <div className="w-24 h-24 rounded-2xl overflow-hidden shadow-2xl ring-4 ring-yellow-400">
                <img src={g.item_image || g.image_url} alt={g.item_name} className="w-full h-full object-cover" />
              </div>
              <div className="mt-2 px-3 py-1 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold shadow-lg whitespace-nowrap">
                @{g.sender_username} → {g.item_name}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Top Supporters banner */}
      {topSupporters.length > 0 && (
        <div className="fixed top-20 left-2 z-[12] flex flex-col gap-1 max-w-[180px]">
          <div className="bg-gradient-to-r from-yellow-500/90 to-orange-500/90 text-white text-xs px-2 py-1 rounded-t-md font-bold flex items-center gap-1">
            <Gift className="w-3 h-3" />
            Top Supporters
          </div>
          <div className="bg-black/60 backdrop-blur-sm rounded-b-md px-2 py-1 space-y-1">
            {topSupporters.map((s, i) => (
              <div key={s.user_id} className="flex items-center gap-2 text-white text-xs">
                <span className="font-bold w-4">{i + 1}</span>
                <Avatar className="w-6 h-6">
                  <AvatarImage src={s.avatar} />
                  <AvatarFallback className="text-[10px] bg-primary/50">
                    {getInitials(s.display_name || s.username)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="truncate">@{s.username}</p>
                  <p className="text-yellow-300 text-[10px]">{formatNumber(s.total_coins)} 🪙</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Send Gift Modal (Coin Shop gifts) */}
      <SendGiftModal
        open={showGiftModal && !isOwner && !!stream}
        onClose={() => setShowGiftModal(false)}
        recipient={stream ? {
          id: stream.user_id,
          username: stream.username,
          display_name: stream.display_name,
          avatar: stream.avatar,
        } : null}
        targetType="live"
        targetId={streamId}
        onSent={() => {
          // Server will broadcast `live_gift` via WS — animation triggers automatically.
        }}
      />

      {/* Battle invite modal (host only) */}
      {isOwner && (
        <BattleInviteModal
          open={showBattleInvite}
          onClose={() => setShowBattleInvite(false)}
          myStreamId={streamId}
          myUserId={user?.id}
          token={token}
          onInvited={(b) => setActiveBattle(b)}
        />
      )}

      {/* Music selector (host only) */}
      {isOwner && (
        <MusicSelector
          open={showMusicSelector}
          onClose={() => setShowMusicSelector(false)}
          streamId={streamId}
          token={token}
          current={musicState}
          onPlay={(m) => setMusicState(m)}
          onStop={() => setMusicState(null)}
        />
      )}

      {/* Hidden audio element for synced background music */}
      <audio ref={musicAudioRef} crossOrigin="anonymous" preload="auto" />
    </div>
  );
}

